import {
  QUANTITY_LEFT_KEY_NAME,
  QUANTITY_LEFT_DEFAULT_VALUE,
  QUANTITY_RIGHT_KEY_NAME,
  QUANTITY_RIGHT_DEFAULT_VALUE,
  VARIANT_ID_LEFT_KEY_NAME,
  VARIANT_ID_RIGHT_KEY_NAME,
} from '~/constants/lens'

const parseQueryQuantity = (
  value: string | undefined,
  { min = 0, max = 9 } = {},
) => {
  const num = parseInt(value ?? '0', 10)
  return Math.max(min, Math.min(max, num))
}

const selectedVariantOptions = reactive<ContactLensValues>({
  left: {
    axis: undefined,
    baseCurve: undefined,
    diameter: undefined,
    addition: undefined,
    contactLensColor: undefined,
    sphere: undefined,
    cylinder: undefined,
  },
  right: {
    axis: undefined,
    baseCurve: undefined,
    diameter: undefined,
    addition: undefined,
    contactLensColor: undefined,
    sphere: undefined,
    cylinder: undefined,
  },
})

export function useLensSelectionState() {
  const router = useRouter()
  const route = useRoute()

  const quantity = computed(() => {
    const currentQuery = route.query
    return {
      left: parseQueryQuantity(currentQuery[QUANTITY_LEFT_KEY_NAME] as string),
      right: parseQueryQuantity(
        currentQuery[QUANTITY_RIGHT_KEY_NAME] as string,
      ),
    }
  })

  const selectedVariantsIds = computed(() => {
    const currentQuery = route.query
    return {
      left: (currentQuery[VARIANT_ID_LEFT_KEY_NAME] as string) ?? '',
      right: (currentQuery[VARIANT_ID_RIGHT_KEY_NAME] as string) ?? '',
    }
  })

  const checkedEyes = computed(() =>
    [
      quantity.value.left > 0 && 'left',
      quantity.value.right > 0 && 'right',
    ].filter((eye): eye is string => {
      return typeof eye === 'string'
    }),
  )

  /*
  Sets provided quantity values to the query params, use defaults if none provided
  */
  const setLensQuantity = (val?: {
    quantityL?: string
    quantityR?: string
  }) => {
    const currentQuery = route.query
    const params = {
      quantityL:
        val && Object.prototype.hasOwnProperty.call(val, 'quantityL')
          ? String(val?.quantityL)
          : currentQuery.quantityL
            ? currentQuery.quantityL
            : QUANTITY_LEFT_DEFAULT_VALUE,
      quantityR:
        val && Object.prototype.hasOwnProperty.call(val, 'quantityR')
          ? String(val?.quantityR)
          : currentQuery.quantityR
            ? currentQuery.quantityR
            : QUANTITY_RIGHT_DEFAULT_VALUE,
    }
    router.replace({ query: { ...currentQuery, ...params } })
  }

  const setLeftLensVariantId = (newValue: string | undefined) => {
    const { [VARIANT_ID_LEFT_KEY_NAME]: _, ...currentQuery } = route.query
    if (!newValue) {
      router.replace({
        query: { ...currentQuery },
      })
    } else {
      router.replace({
        query: { ...currentQuery, [VARIANT_ID_LEFT_KEY_NAME]: newValue },
      })
    }
  }

  const setRightLensVariantId = (newValue: string | undefined) => {
    const { [VARIANT_ID_RIGHT_KEY_NAME]: _, ...currentQuery } = route.query
    if (!newValue) {
      router.replace({
        query: { ...currentQuery },
      })
    } else {
      router.replace({
        query: { ...currentQuery, [VARIANT_ID_RIGHT_KEY_NAME]: newValue },
      })
    }
  }

  const setLeftSelectedOption = (lensValues: ContactLensValue) => {
    selectedVariantOptions.left = lensValues
  }

  const setRightSelectedOption = (lensValues: ContactLensValue) => {
    selectedVariantOptions.right = lensValues
  }

  const isSelectedVariantIdSet = (eyePositionIndex: 'left' | 'right') =>
    Boolean(selectedVariantsIds.value[eyePositionIndex].length)

  return {
    quantity,
    selectedVariantsIds,
    setLensQuantity,
    setLeftLensVariantId,
    setRightLensVariantId,
    checkedEyes,
    selectedVariantOptions,
    setLeftSelectedOption,
    setRightSelectedOption,
    isSelectedVariantIdSet,
  }
}
